import React from 'react';
import { MaterialIcons } from '@iclinic/design-system';

import { Promotion } from '../services/types';
import { monetaryFormatter } from 'shared/utils/formatters';

const { Contacts, AssignmentTurnedInRounded, Forum } = MaterialIcons;

type AddonContent = {
  description: (
    value?: string,
    recurrence?: string,
    promotion?: Promotion,
  ) => React.ReactNode;
  icon: React.ReactElement;
};

type AddonContentByPlan = {
  [key: string]: {
    [key: string]: AddonContent;
  };
};

type TitleModalByAddonSlug = {
  [key: string]: (isAnnual: boolean) => string;
};

const renderAddonDescription = (
  title: string,
  text: string,
  value?: string,
  recurrence?: string,
  promotion?: Promotion,
): React.ReactNode => {
  const recurrenceSelected = recurrence === 'ano' ? 'annual' : 'monthly';
  const promoValue =
    recurrence && promotion?.frequencies?.[recurrenceSelected]?.value;

  return (
    <>
      <strong>{title}</strong>
      <br />
      {text} —{' '}
      {promoValue ? (
        <>
          de R$ {value}/{recurrence} por R$ {monetaryFormatter(promoValue)}/
          {recurrence}
        </>
      ) : (
        <>
          por R$ {value}/{recurrence}
        </>
      )}
    </>
  );
};

const telemedicine: AddonContent = {
  description: (value, recurrence, promotion) =>
    renderAddonDescription(
      '10 teleconsultas por mês',
      'Realize atendimentos remotos com teleconsultas integradas ao prontuário',
      value,
      recurrence,
      promotion,
    ),
  icon: <Contacts />,
};

const electronicSignature: AddonContent = {
  description: (value, recurrence, promotion) =>
    renderAddonDescription(
      'Assinatura eletrônica ilimitada de documentos',
      'Agilize a rotina do consultório com a assinatura digital integrada ao prontuário',
      value,
      recurrence,
      promotion,
    ),
  icon: <AssignmentTurnedInRounded />,
};

const messageBasic: AddonContent = {
  description: (value, recurrence, promotion) =>
    renderAddonDescription(
      '85 disparos automáticos via WhatsApp por mês',
      'Lembretes, confirmações de consulta e pesquisas de satisfação',
      value,
      recurrence,
      promotion,
    ),
  icon: <Forum />,
};

export const addonsContentByPlan: AddonContentByPlan = {
  premium: {
    telemedicine: {
      description: () => 'Teleconsultas ilimitadas neste plano',
      icon: <Contacts />,
    },
    'electronic-signature': electronicSignature,
    'message-basic': messageBasic,
  },
  pro: {
    telemedicine,
    'electronic-signature': electronicSignature,
    'message-basic': messageBasic,
  },
  plus: {
    telemedicine,
    'electronic-signature': electronicSignature,
    'message-basic': messageBasic,
  },
  starter: {
    telemedicine,
    'electronic-signature': electronicSignature,
    'message-basic': messageBasic,
  },
};

export const titleModalSelectPhysiciansByAddonSlug: TitleModalByAddonSlug = {
  telemedicine: () =>
    `Você está adicionando um pacote de 10 teleconsultas mensais na sua assinatura.`,
  'electronic-signature': () =>
    `Você está adicionando o pacote de assinatura eletrônica.`,
  'message-basic': () =>
    `Você está adicionando o pacote de 85 mensagens mensais de confirmação automática na sua assinatura.`,
};
